import { DateTime } from 'luxon';
import { assetStrategyKey } from './assetKeys';

export type ScheduleWarningType = 'OVERLAP' | 'PAST_EVENT_END' | 'COVERAGE_GAP';

/** An uncovered interval within a title's scheduled coverage (ISO timestamps, UTC). */
export interface ScheduleGap {
  startTime: string;
  endTime: string;
}

export interface ScheduleWarning {
  type: ScheduleWarningType;
  /** Composite (assetId, productStrategyId) key of the row this warning applies to. */
  key: string;
  /** COVERAGE_GAP only: display name of the affected title (falls back to the GTI). */
  titleName?: string;
  /** COVERAGE_GAP only: the uncovered intervals between the group's earliest start and latest end. */
  gaps?: ScheduleGap[];
}

export interface Schedule {
  startTime: string;
  endTime: string;
}

/**
 * Minimal asset shape required for schedule-warning detection.
 * Mirrors the relevant subset of `AssetDto`.
 */
export interface SchedulableAsset {
  assetId: string;
  productStrategyId?: string;
  titles: Array<{ gti: string; name?: string }>;
  /** Campaign owning this asset. Warnings are scoped per campaign + GTI + strategy. */
  campaignId?: string;
  /** High-value message (HVM), e.g. LIVE_NOW / TODAY / TOMORROW. Used to scope overlap. */
  metadata?: { liveStatus?: string };
}

interface ScheduledAsset {
  /** Composite (assetId, productStrategyId) key. */
  key: string;
  gti: string;
  /** Title display name (used in coverage-gap details; falls back to the GTI). */
  titleName: string | undefined;
  /** HVM (liveStatus); `undefined` when the asset has no live status. */
  liveStatus: string | undefined;
  start: number;
  end: number;
}

/**
 * Formats coverage-gap intervals for display, e.g. "May 22, 02:00 – May 22, 04:00".
 * Times are rendered in UTC with the same format as the asset card's schedule display.
 */
export function formatScheduleGapRanges (gaps: ScheduleGap[]): string {
  const format = (iso: string) => DateTime.fromISO(iso, { zone: 'utc' }).toFormat('MMM dd, HH:mm');
  return gaps.map((gap) => `${format(gap.startTime)} – ${format(gap.endTime)}`).join('; ');
}

/**
 * Computes non-blocking schedule warnings for sports asset approval.
 *
 * Three rules, all scoped per (campaign × title GTI × product strategy). Assets for the same title
 * in different campaigns or strategies never warn against each other.
 *  1. OVERLAP        — two assets in the same group with *different* HVMs (liveStatus) have
 *                      intersecting live windows ([start, end)). Same-HVM overlap is expected —
 *                      different sizes of the same creative message (e.g. two "LIVE TOMORROW"
 *                      banners in different dimensions) are meant to be live simultaneously. Only
 *                      cross-HVM overlap is problematic (e.g. "LIVE TODAY" alongside "LIVE
 *                      TOMORROW" on the same title at the same time).
 *  2. PAST_EVENT_END — an asset's Taken Down (end) time is after the event's predicted end time.
 *  3. COVERAGE_GAP   — for a group, the union of live windows is not contiguous between the earliest
 *                      start and the latest end (a period with no asset live). Flags every asset in
 *                      the affected group, since the gap is a group-level property. The warning
 *                      carries the title name and every uncovered interval so the UI can show
 *                      exactly where coverage is missing.
 *
 * Assets with no schedule, an invalid schedule, or `titles.length !== 1` are skipped — consistent
 * with the schedule pre-fill logic in StandaloneAssetsContainer.
 *
 * The caller is responsible for passing the *full* set of assets under consideration (all pages) —
 * overlap and coverage-gap are cross-asset properties and cannot be detected within a single page.
 *
 * @returns Map of assetStrategyKey -> warnings for that row (only rows with a warning appear).
 *          The `schedules` and `predictedEndByAsset` inputs are keyed the same way.
 */
export function computeScheduleWarnings (
  assets: SchedulableAsset[],
  schedules: Map<string, Schedule>,
  predictedEndByAsset: Map<string, string>,
): Map<string, ScheduleWarning[]> {
  const warningsByAsset = new Map<string, ScheduleWarning[]>();

  const addWarning = (
    key: string,
    type: ScheduleWarningType,
    detail?: Pick<ScheduleWarning, 'titleName' | 'gaps'>,
  ) => {
    const existing = warningsByAsset.get(key) ?? [];
    if (!existing.some((w) => w.type === type)) {
      existing.push({ type, key, ...detail });
    }
    warningsByAsset.set(key, existing);
  };

  // Collect rows with a valid schedule, grouped by (campaign × GTI × strategy).
  const byGroup = new Map<string, ScheduledAsset[]>();
  for (const asset of assets) {
    if (asset.titles.length !== 1) continue;
    const gti = asset.titles[0]?.gti;
    if (!gti) continue;

    const key = assetStrategyKey(asset);
    const schedule = schedules.get(key);
    if (!schedule?.startTime || !schedule?.endTime) continue;

    const start = new Date(schedule.startTime).getTime();
    const end = new Date(schedule.endTime).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) continue;

    // Rule 2: past event end (per row, independent of grouping).
    const predictedEnd = predictedEndByAsset.get(key);
    if (predictedEnd) {
      const predictedEndMs = new Date(predictedEnd).getTime();
      if (!Number.isNaN(predictedEndMs) && end > predictedEndMs) {
        addWarning(key, 'PAST_EVENT_END');
      }
    }

    const groupKey = `${asset.campaignId ?? ''}::${gti}::${asset.productStrategyId ?? ''}`;
    const group = byGroup.get(groupKey) ?? [];
    group.push({
      key,
      gti,
      titleName: asset.titles[0]?.name,
      liveStatus: asset.metadata?.liveStatus,
      start,
      end,
    });
    byGroup.set(groupKey, group);
  }

  // Rules 1 & 3: per-group overlap and coverage gap.
  for (const group of Array.from(byGroup.values())) {
    if (group.length < 2) continue;

    const sorted: ScheduledAsset[] = [...group].sort((a, b) => a.start - b.start);

    // Overlap: any pair of intersecting [start, end) windows with *different* HVMs (liveStatus).
    // Same-HVM overlap is expected (different sizes of the same creative message are meant to be
    // live simultaneously). Cross-HVM overlap means two different messages are shown to users at
    // once (e.g. "LIVE TODAY" alongside "LIVE TOMORROW"), which is the problematic case.
    // Assets with no liveStatus are treated as a distinct group (can overlap each other but not
    // assets with a defined liveStatus).
    for (let i = 0; i < sorted.length; i++) {
      const a: ScheduledAsset = sorted[i];
      for (let j = i + 1; j < sorted.length; j++) {
        const b: ScheduledAsset = sorted[j];
        if (a.liveStatus === b.liveStatus) continue;
        if (a.start < b.end && b.start < a.end) {
          addWarning(a.key, 'OVERLAP');
          addWarning(b.key, 'OVERLAP');
        }
      }
    }

    // Coverage gap: sweep merged windows; a hole exists wherever the running max-end falls
    // strictly before the next window's start. Collect every hole so the UI can show the exact
    // uncovered intervals.
    const gaps: ScheduleGap[] = [];
    let coveredUntil = sorted[0].end;
    for (let i = 1; i < sorted.length; i++) {
      const current: ScheduledAsset = sorted[i];
      if (current.start > coveredUntil) {
        gaps.push({
          startTime: new Date(coveredUntil).toISOString(),
          endTime: new Date(current.start).toISOString(),
        });
      }
      coveredUntil = Math.max(coveredUntil, current.end);
    }
    if (gaps.length > 0) {
      const titleName = sorted.find((m) => m.titleName)?.titleName ?? sorted[0].gti;
      for (const member of group) {
        addWarning(member.key, 'COVERAGE_GAP', { titleName, gaps });
      }
    }
  }

  return warningsByAsset;
}
